import React, { useState, FunctionComponent, useCallback } from 'react';
import { Checkbox, Link, LinkButton, SliderNumberInput, Typography } from '@vtblife/uikit';
import classname from '@search/classname/src';
import './styles.css';
import { formatNumber } from '@search/helpers/src/formatNumber';
import { useUserDetailsFetcher } from '@search/auth/src/userDetails/userDetails';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import { AuthInfo } from '@search/auth/src/AuthInfo';
import { UseAuthError } from '@search/auth/src/useAuth';
import { MortgagePurpose } from '@search/graphql-typings/src';
import { useClassifiedOfferDataLayerPush } from '@search/vtbeco-frontend-core/domain/offer/analytics/ClassifiedOfferGtmContext';
import { useMortgageAppSource } from '../MortgageAppSourceContext';
import { minimalBanksMortgageUrlCreate } from '../minimalBanksMortgageUrl';
import { windowOpen } from '../mortgage2/useMortgage2Request';
import Image from '../../Image';
import { mortgage2ParamsFromClasified } from '../mortgage2/mortgage2ParamsFromClasified';
import { LAND_MORTGAGE_STEP, LandMortgageActionType, getMortgagePaymentRange } from './constants';
import { LandMortgageCalcModal } from './LandMortgageCalcModal';
import { useLandMortgageForm } from './useLandMortgageForm';
import { MortgageOffer } from './MortgageOffer';

const cn = classname.bind(null, 'LandMortgage');

interface LandMortgageProps {
    propertyCost?: number;
    isMobile?: boolean;
    regionId: number;
}

export const LandMortgage: FunctionComponent<LandMortgageProps> = ({
    isMobile = false,
    propertyCost = 0,
    regionId
}) => {
    const { minPayment, maxPayment, initalPrice } = getMortgagePaymentRange(propertyCost);

    const [ payment, setPayment ] = useState(initalPrice);
    const [ isLandPriceIncluded, setLandPriceIncluded ] = useState(true);
    const [ isOpenModal, setOpenModal ] = useState(false);
    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const sendAnalytics = (actionType: LandMortgageActionType) => dataLayerPush({
        event: 'up_action',
        theme: 'ipoteka',
        page_type: 'card',
        type: actionType
    });

    const {
        calculatedValues: {
            creditSum,
            tipPayment,
            imgSrc
        },
        isPaymentLower
    } = useLandMortgageForm({
        payment,
        propertyCost,
        isLandPriceIncluded,
        isMobile
    });

    const handleInput = (value: number) => {
        setPayment(isNaN(value) ? 0 : value);
    };

    const openOrAuth = (authInfo?: AuthInfo) => {
        return new Promise((resolve, reject) => {
            if (authInfo?.isAuthenticated) {
                resolve(null);
            } else {
                reject();
            }
        });
    };

    const userDetailsFetcher = useUserDetailsFetcher();
    const { waitAuth } = useM2AuthContext();
    const sourceMortgage = useMortgageAppSource();

    const params = mortgage2ParamsFromClasified({
        purpose: MortgagePurpose.Php,
        regionId,
        term: 30,
        propertyCost: creditSum,
        ownAmount: creditSum * 0.2
    });

    const openLink = useCallback((actionType: LandMortgageActionType) => {
        waitAuth(
            authInfo => openOrAuth(authInfo)
                .then(() => userDetailsFetcher())
                .catch((e) => {
                    throw new UseAuthError();
                }),
            { continueAfterLogin: true }
        )
            .then((userDetails) => {
                const { fullUrl: minimalBanksMortgageUrl } = minimalBanksMortgageUrlCreate({
                    isAuthenticated: true,
                    ...params,
                    mobilePhone: userDetails.data?.userDetails?.phone ?? '',
                    source: sourceMortgage
                });

                windowOpen(minimalBanksMortgageUrl, { isMobile });
            })
            // eslint-disable-next-line no-console
            .catch(e => console.error(e));

        sendAnalytics(actionType);
    }, [ dataLayerPush ]);

    const openModal = useCallback(() => {
        setOpenModal(true);

        sendAnalytics(LandMortgageActionType.IzhsCalc);
    }, [ dataLayerPush ]);

    return (
        <div className={cn()}>
            <Typography variant='promo-h3' className={cn('title')}>
                Возьмите ипотеку и постройте дом за три простых шага
            </Typography>
            <div className={cn('steps')}>
                <Typography color='link' onClick={() => openLink(LandMortgageActionType.IzhsReq)}>Оставьте заявку на ипотеку</Typography>
                <Typography>→</Typography>
                <Typography>Получите одобрение</Typography>
                <Typography>→</Typography>
                <Typography>Приступайте к строительству</Typography>
            </div>
            <div className={cn('form-container')}>
                <div className={cn('left')}>
                    <div className={cn('field', { isPaymentLower })}>
                        <Typography className={cn('label')}>Сколько вы готовы платить в месяц?</Typography>
                        <SliderNumberInput
                            currency='RUB'
                            size='l'
                            inputMode='decimal'
                            logarithmic
                            max={maxPayment}
                            min={minPayment}
                            step={LAND_MORTGAGE_STEP}
                            name='payment'
                            onChange={handleInput}
                            value={payment}
                            onChangeDelay={500}
                            postfix={'₽/мес.'}
                            onReset={false}
                        />
                        {isPaymentLower && <Typography variant='secondary-alone' className={cn('error-tip')}>доход должен быть не менее: {formatNumber(tipPayment)} ₽/мес.</Typography>}
                        <div className={cn('checkbox')}>
                            <Checkbox
                                value={isLandPriceIncluded}
                                onChange={() => setLandPriceIncluded(state => ! state)}
                            >
                                Включить стоимость участка в ипотеку
                            </Checkbox>
                        </div>
                    </div>
                    <MortgageOffer
                        creditSum={creditSum}
                        isEmpty={isPaymentLower}
                        imgSrc={imgSrc}
                        isMobile={isMobile}
                        modalHandler={openModal}
                    />
                    {
                        ! isMobile &&
                        <LinkButton
                            variant='accent'
                            disabled={isPaymentLower}
                            fullWidth
                            onClick={openModal}
                            size='m'
                        >
                            Посмотреть расчёт
                        </LinkButton>
                    }
                </div>
                <div className={cn('right')}>
                    { imgSrc && <Image url={imgSrc} loading='lazy' alt='дом' />}
                    <Typography variant='secondary-alone' className={cn('image-tag')}>Таким может быть ваш дом. Cоздано нейросетью</Typography>
                </div>
            </div>
            <div className={cn('disclaimer')}>
                {
                    isMobile ?
                        <Typography color='secondary' variant='secondary-alone'>
                            Не&nbsp;является публичной офертой. Финальные условия кредитования определяются при заключении договора.
                        </Typography> :
                        <Typography color='secondary' variant='secondary-alone'>
                            Не&nbsp;является публичной офертой. Подробнее о&nbsp;строительстве дома <Link href='https://m2.ru/ipoteka/stroitelstvo-doma/' target='blank'>здесь</Link>. Финальные условия кредитования определяются при заключении договора.
                        </Typography>
                }
            </div>
            {isOpenModal &&
                <LandMortgageCalcModal
                    onClose={() => setOpenModal(false)}
                    creditSum={creditSum}
                    isMobile={isMobile}
                    onRequest={openLink}
                />}
        </div>
    );
};
